import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCents, formatCount, formatEpc, formatPercent } from '@affily/shared/money';
import { Seo } from '../../components/Seo.jsx';
import {
  DEFAULT_TERMS,
  TermsInputs,
  useProjection,
  ResultRow,
  RetentionField,
  FeeNote,
} from '../../components/Calculator.jsx';
import { Button, Field, Input, SectionTitle } from '../../components/ui.jsx';

/**
 * Creator earnings calculator.
 *
 * Answers "what will I actually make from this?" — the question a creator has
 * before deciding whether a program is worth their audience's attention. The
 * defaults are deliberately unflattering: a 2% click rate and a 2% conversion
 * rate are realistic, and a calculator that quietly assumes 10% would be
 * lying in exactly the way this product exists to avoid.
 */
export default function EarningsCalculator() {
  const [terms, setTerms] = useState({ ...DEFAULT_TERMS, recurringMode: 'fixed_period' });
  const [funnel, setFunnel] = useState({
    audience: '50000',
    reachRatePct: '30',
    clickRatePct: '2',
    posts: '1',
    conversionRatePct: '2',
    orderAmount: '50',
    cycleRetentionPct: '90',
  });

  const setTerm = (key) => (e) => setTerms((f) => ({ ...f, [key]: e.target.value }));
  const setFunnelValue = (key) => (e) => setFunnel((f) => ({ ...f, [key]: e.target.value }));

  const assumptions = {
    audience: Number(funnel.audience) || 0,
    reachRatePct: Number(funnel.reachRatePct) || 0,
    clickRatePct: Number(funnel.clickRatePct) || 0,
    posts: Number(funnel.posts) || 1,
    conversionRatePct: Number(funnel.conversionRatePct) || 0,
    orderAmountCents: Math.round(Number(funnel.orderAmount || 0) * 100),
    cycleRetentionPct: Number(funnel.cycleRetentionPct) || 100,
  };

  const { projection, terms: realTerms } = useProjection(terms, assumptions);
  const recurring = realTerms?.recurringMode === 'fixed_period';

  return (
    <>
      <Seo
        title="Affiliate earnings calculator for creators"
        description="Work out what an affiliate or UGC partnership will actually pay you — including recurring commissions. Free, no signup."
      />

      <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
        <p className="text-2xs font-semibold uppercase tracking-widest text-accent">Free tool</p>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight">
          Affiliate earnings calculator
        </h1>
        <p className="mt-3 max-w-xl text-ink-muted">
          What a partnership is likely to pay you, before you spend a post on it. The defaults are
          realistic rather than flattering — adjust them to your own numbers.
        </p>

        <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
            <section>
              <SectionTitle hint="Copy these from the program's public page">
                The program’s terms
              </SectionTitle>
              <TermsInputs form={terms} set={setTerm} />
            </section>

            <section>
              <SectionTitle hint="Your own numbers">Your audience</SectionTitle>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Audience size">
                  <Input
                    type="number"
                    min="0"
                    value={funnel.audience}
                    onChange={setFunnelValue('audience')}
                  />
                </Field>
                <Field label="Posts about it">
                  <Input
                    type="number"
                    min="1"
                    value={funnel.posts}
                    onChange={setFunnelValue('posts')}
                  />
                </Field>
                <Field label="Reach (%)" hint="Share of your audience who see a post.">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={funnel.reachRatePct}
                    onChange={setFunnelValue('reachRatePct')}
                  />
                </Field>
                <Field label="Click rate (%)" hint="Of those who see it. 1–3% is typical.">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={funnel.clickRatePct}
                    onChange={setFunnelValue('clickRatePct')}
                  />
                </Field>
              </div>
            </section>

            <section>
              <SectionTitle hint="What happens after the click">The brand’s side</SectionTitle>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Conversion rate (%)"
                  hint="Share of clicks that buy. Affily publishes this on every program page."
                >
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={funnel.conversionRatePct}
                    onChange={setFunnelValue('conversionRatePct')}
                  />
                </Field>
                <Field label="Average order (USD)">
                  <Input
                    type="number"
                    min="0"
                    value={funnel.orderAmount}
                    onChange={setFunnelValue('orderAmount')}
                  />
                </Field>
                {recurring && (
                  <RetentionField
                    value={funnel.cycleRetentionPct}
                    onChange={setFunnelValue('cycleRetentionPct')}
                    audience="creator"
                  />
                )}
              </div>
            </section>
          </form>

          <aside className="lg:sticky lg:top-20 lg:self-start">
            <div className="rounded-lg border border-rule bg-paper-raised p-5">
              <h2 className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                What you’d earn
              </h2>

              {projection ? (
                <>
                  <p className="mt-2 text-4xl font-semibold tabular-nums text-accent">
                    {formatCents(projection.netCents)}
                  </p>
                  <p className="text-xs text-ink-faint">in commission, before Affily’s fee (shown in your account)</p>

                  <div className="mt-4 border-t border-rule pt-2">
                    <ResultRow label="Clicks" value={formatCount(projection.clicks)} />
                    <ResultRow
                      label="Conversions"
                      value={formatCount(Math.floor(projection.conversions))}
                    />
                    <ResultRow
                      label="EPC"
                      value={projection.epc === null ? null : formatEpc(projection.epc)}
                      empty="No clicks, so there is nothing to divide by"
                      hint="What each click is worth"
                    />
                    <ResultRow label="Commission" value={formatCents(projection.grossCents)} emphasis />
                  </div>

                  {recurring && (
                    <div className="mt-4 border-t border-rule pt-3">
                      <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                        Because it recurs
                      </p>
                      <ResultRow
                        label="One customer is worth"
                        value={formatCents(projection.perConversion.lifetimeNetCents)}
                        hint={`net, across all ${projection.contractedCycles} cycles`}
                      />
                      <ResultRow
                        label="Expected cycles paid"
                        value={projection.effectiveCycles.toFixed(1)}
                        hint={`of ${projection.contractedCycles} contracted, after churn`}
                      />
                    </div>
                  )}
                </>
              ) : (
                <p className="mt-2 text-sm text-ink-muted">Fill in the terms to see a projection.</p>
              )}

              <FeeNote audience="creator" />
            </div>

            <div className="mt-4 rounded-lg border border-rule bg-paper-sunken p-4">
              <p className="text-sm font-medium">Stop guessing the conversion rate</p>
              <p className="mt-1 text-xs text-ink-muted">
                Every program on Affily publishes its real, tracked conversion rate — including the
                bad ones. You can use the actual number instead of an assumption.
              </p>
              <Button to="/programs" size="sm" className="mt-3">
                Browse programs
              </Button>
            </div>
          </aside>
        </div>

        <section className="mt-16 max-w-2xl">
          <h2 className="text-xl font-semibold tracking-tight">
            Why a lower recurring rate often beats a higher one-off
          </h2>
          <p className="mt-3 text-ink-muted">
            A 30% one-time commission on a $50 subscription pays you $15, once. A 10% commission
            that recurs for 12 billing cycles pays $5 a month — $60 if the customer stays the year,
            and around {formatPercent(36, { digits: 0 })} of that even at a fairly brisk churn rate.
            The headline rate is the smaller half of the question.
          </p>
          <p className="mt-3 text-ink-muted">
            That is why Affily prints the duration next to every rate rather than after a click:
            two programs both advertising “20%” are not the same offer, and you shouldn’t have to
            open both to find out.
          </p>
          <Link
            to="/tools/affiliate-program-calculator"
            className="mt-4 inline-block text-sm font-medium text-accent hover:underline"
          >
            Running a program instead? Use the brand-side calculator →
          </Link>
        </section>
      </div>
    </>
  );
}
